'use client';

import { NavigableButton } from '@carvana/ds-button';
import CarSwap from '@carvana/ds-icons/TradeinSpot';
import VehicleFront from '@carvana/ds-icons/VehicleFrontSpot';
import Link from '@carvana/ds-link';
import { icon } from '@carvana/ds-styles';
import Switch from '@carvana/ds-switch';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { NavPayloadAction } from '@carvana/merch-native';
import { isSuccess, type Result } from '@carvana/merch-result';
import { datadogRum } from '@datadog/browser-rum';
import { use, useContext, useOptimistic } from 'react';
import { z } from 'zod';
import { toggleTradeInAction } from '~/app/(carvana-layout)/financing-rsc/actions';
import Image from '~/components/common/Image';
import { ANALYTICS_EVENTS } from '~/constants/flp/analyticsEvents';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import { useIsNative } from '~/context/common/DeviceContext';
import { reducer } from '~/context/flp/Reducers';
import { usePricingUpdate } from '~/context/flp-rsc/PricingUpdateContext';
import { PricingAggregates, TradeInStatus } from '~/contracts/flp';
import useToggleTracking from '~/hooks/flp-rsc/useToggleTracking';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import {
  Badge,
  BadgeIcon,
  BadgeText,
  Thumbnail,
  TradeInVehicle,
  Value,
  VehicleDetails,
  Ymm,
  YmmValue
} from './TradeIn.styles';
import FinanceCard from '../FinanceCard';

const textOptions = {
  default:
    'You could be eligible for tax savings by trading in your current vehicle.',
  enabled: 'Terms include your trade-in vehicle.',
  disabled: "Terms don't include your trade-in vehicle.",
  error:
    'We are having trouble retrieving your trade-in information. This will not be reflected in your pricing.',
  expired: 'Terms cannot include a trade-in vehicle with an expired offer.'
};

const TradeInfoSchema = z.object({
  applyTradeIn: z.boolean(),
  appraisalId: z.number().optional(),
  make: z.string(),
  mobileImage: z.string().nullable(),
  model: z.string(),
  status: z.string(),
  totalTradeInValue: z.number(),
  trim: z.string(),
  year: z.number()
});

const NO_TRADE_IN = { enabled: false, data: null, error: null } as const;

function getTradeIn(result: Result<PricingAggregates> | undefined) {
  if (!result || !isSuccess(result)) {
    return NO_TRADE_IN;
  }

  const parsed = TradeInfoSchema.optional().safeParse(result.content.tradeInfo);

  if (!parsed.success) {
    return NO_TRADE_IN;
  }

  return reducer.tradein(result.content);
}

type OwnProps = {
  pricingAggregates: Promise<Result<PricingAggregates> | undefined>;
};

export default function TradeIn({ pricingAggregates }: OwnProps) {
  const result = use(pricingAggregates);
  const tradeIn = getTradeIn(result);

  const isNative = useIsNative();
  const { data: tradeInData, error, enabled } = tradeIn || {};
  const { trackEvent } = useContext(AnalyticsContext);
  const { isPending, startTransition } = usePricingUpdate();
  const trackToggle = useToggleTracking({
    analyticsClickEvent: ANALYTICS_EVENTS.PREQUALIFIED.TOGGLES_TRADEIN,
    datadogClickEvent: DATADOG_EVENTS.PREQUALIFIED.TOGGLES_TRADEIN,
    errorEvent: DATADOG_EVENTS.API.TOGGLE_TRADEIN_API_ERROR,
    location: 'Toggle Trade-In',
    toggleType: 'tradein'
  });

  const [optimisticApplyTradeIn, setOptimisticApplyTradeIn] = useOptimistic(
    Boolean(tradeInData?.applyTradeIn)
  );
  const status = tradeInData?.status?.toLowerCase() || '';

  const hasError = !!error;
  const isDefault = !hasError && !enabled;

  const hasExpiredTrade = status === TradeInStatus.Expired;
  const badgeText = hasExpiredTrade
    ? `Offer ${status.charAt(0).toUpperCase() + status.slice(1)}`
    : '';

  let text = textOptions.default;
  if (hasError) {
    text = textOptions.error;
  } else if (!!textOptions[status as keyof typeof textOptions]) {
    text = textOptions[status as keyof typeof textOptions];
  } else if (enabled && !optimisticApplyTradeIn) {
    text = textOptions.disabled;
  } else if (enabled) {
    text = textOptions.enabled;
  }

  const handleToggle = () => {
    const nextStatus = !optimisticApplyTradeIn;

    startTransition(() =>
      trackToggle(nextStatus, () => {
        setOptimisticApplyTradeIn(nextStatus);

        return toggleTradeInAction(nextStatus);
      })
    );
  };

  const CardToggle = () => {
    if (enabled && !hasExpiredTrade) {
      return (
        <Switch
          aria-label="Apply trade-in to terms"
          checked={optimisticApplyTradeIn}
          disabled={isPending}
          onChange={handleToggle}
        />
      );
    }

    return null;
  };

  const handleAddTradeIn = () => {
    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.CLICKS_ADD_TRADEIN);
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.CLICKS_ADD_TRADEIN);
  };

  const handleEditTradeIn = () => {
    datadogRum.addAction(
      DATADOG_EVENTS.PREQUALIFIED.CLICKS_VIEW_TRADEIN_DETAILS
    );
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.CLICKS_VIEW_TRADEIN_DETAILS);
  };

  const vehicleTitle = `${tradeInData?.year} ${tradeInData?.make} ${tradeInData?.model} ${tradeInData?.trim}`;
  const vehicleImage =
    tradeInData?.mobileImage && !tradeInData.mobileImage.includes('generic')
      ? tradeInData.mobileImage
      : null;

  if (tradeIn === null) {
    return null;
  }

  return (
    <FinanceCard
      heading="Trade-in"
      id="trade-in"
      icon={<CarSwap color={icon.primary} size={24} />}
      text={text}
      disabled={hasError}
      Switch={<CardToggle />}
      showDivider={enabled || hasExpiredTrade}
    >
      {isDefault && (
        <NavigableButton
          className="self-start"
          data-analytics-id="Financing Trade-In Add Button"
          data-native-payload={JSON.stringify({
            action: NavPayloadAction.ViewTrades
          })}
          href="/sell-my-car"
          size="sm"
          variant="secondary"
          onClick={handleAddTradeIn}
          data-testid="add-trade-in"
        >
          Add a Trade-In
        </NavigableButton>
      )}

      {(enabled || hasExpiredTrade) && (
        <TradeInVehicle>
          <Thumbnail>
            {vehicleImage ? (
              <Image
                src={vehicleImage}
                alt={vehicleTitle}
                width={88}
                height={88}
              />
            ) : (
              <VehicleFront className="p-20" size={88} />
            )}
          </Thumbnail>
          <VehicleDetails>
            <YmmValue>
              {hasExpiredTrade && (
                <Badge status={status}>
                  <BadgeIcon />
                  <BadgeText>{badgeText}</BadgeText>
                </Badge>
              )}
              <Ymm>
                {tradeInData?.year} {tradeInData?.make} {tradeInData?.model}{' '}
                {tradeInData?.trim}
              </Ymm>
              {!hasExpiredTrade && (
                <Value>
                  Value: $
                  {formatAndDropDecimal(tradeInData?.totalTradeInValue ?? 0)}
                </Value>
              )}
              <Link
                data-analytics-id="Financing Trade-In View Details Button"
                data-native-payload={JSON.stringify({
                  action: NavPayloadAction.ViewTrades
                })}
                href={
                  !isNative &&
                  tradeInData?.appraisalId &&
                  tradeInData.appraisalId > 0
                    ? `/sell-my-car/offer/${tradeInData.appraisalId}`
                    : '/sell-my-car'
                }
                size="sm"
                onClick={handleEditTradeIn}
                data-testid="view-trade-details"
              >
                View Details
              </Link>
            </YmmValue>
          </VehicleDetails>
        </TradeInVehicle>
      )}
    </FinanceCard>
  );
}
